/**
 * Group headers and dividers for `v-autocomplete` / `v-select` item lists.
 *
 * Vuetify has no grouping of its own for these components, so a group is expressed as
 * sentinel rows inside the flat `items` array. Both sentinels carry
 * `props: { disabled: true }`: Vuetify spreads each item's `props` onto its internal item
 * and `select()` returns early on `props.disabled`, so that flag is the only thing keeping
 * a header or divider out of the model. Removing it makes them selectable.
 */

export interface SelectGroupHeader {
  id: string;
  header: string;
  props: { disabled: true };
}

export interface SelectGroupDivider {
  id: string;
  divider: true;
  props: { disabled: true };
}

export type SelectGroupEntry<TItem> = TItem | SelectGroupHeader | SelectGroupDivider;

export interface SelectGroup<TItem> {
  header: string;
  items: readonly TItem[];
}

const DISABLED_ITEM_PROPS = { disabled: true } as const;

export function isSelectGroupHeader<TItem extends object>(
  entry: SelectGroupEntry<TItem>
): entry is SelectGroupHeader {
  return 'header' in entry;
}

export function isSelectGroupDivider<TItem extends object>(
  entry: SelectGroupEntry<TItem>
): entry is SelectGroupDivider {
  return 'divider' in entry;
}

export function isSelectGroupItem<TItem extends object>(
  entry: SelectGroupEntry<TItem>
): entry is TItem {
  return !isSelectGroupHeader(entry) && !isSelectGroupDivider(entry);
}

/**
 * Flattens groups into `[header, ...items, divider, header, ...items]`, dropping empty
 * groups so a header never renders above nothing and no trailing divider is emitted.
 *
 * `idPrefix` keeps sentinel ids from colliding with entity ids (and with another select's
 * sentinels) in Vuetify's internal item map.
 */
export function buildSelectGroupEntries<TItem extends object>(
  groups: ReadonlyArray<SelectGroup<TItem>>,
  idPrefix: string
): SelectGroupEntry<TItem>[] {
  const populatedGroups = groups.filter(group => group.items.length > 0);

  return populatedGroups.flatMap((group, index) => {
    const entries: SelectGroupEntry<TItem>[] = [
      { id: `${idPrefix}-group-${index}`, header: group.header, props: DISABLED_ITEM_PROPS },
      ...group.items
    ];

    if (index < populatedGroups.length - 1) {
      entries.push({
        id: `${idPrefix}-group-divider-${index}`,
        divider: true,
        props: DISABLED_ITEM_PROPS
      });
    }

    return entries;
  });
}
